import ZenDeskScript from '@sls-react/components/Layout/ZenDeskScript';
import GlobalContexts from '@sls-react/contexts/GlobalContexts';
import { Router } from '@sls-react/routing';
import GlobalStyle from '@sls-react/theme/GlobalStyle';
import getEnv from '@sls-react/utils/getEnv';
import App from 'next/app';
import dynamic from 'next/dynamic';
import NProgress from 'nprogress';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
// noinspection ES6UnusedImports
import {} from 'styled-components/cssprop';
import setupSentry from '../services/sentry/setupSentry';
import '../styles/index.css';
import Cookies from 'js-cookie';
import { prepareWebVitals } from '@sls-react/services/analytics/webVitals';
import getInitialClientProps, {
  setCacheOnClient,
} from '@sls-react/lib/initialData/getInitialClientProps';
import { InitialData } from '@sls-react/lib/initialData/initialData';
import ScrollRestoration from '@sls-react/components/ScrollRestoration';
import analyticsPageView from '@sls-react/services/analytics/analyticsPageView';
import analyticsInitialize from '@sls-react/services/analytics/analyticsInitialize';
import ReactQueryApp from '@sls-react/components/ReactQueryApp';
import { KnownCookie } from '@sls-react/typings/cookies';
import { mockDevServer } from '../test/dev-server';
import { ReactQueryDevtools } from 'react-query/devtools';
import { wrapper } from '@sls-react/store/store';

const AutomaticPopupModal = dynamic(
  () => import('@sls-react/components/LoginRegister/AutomaticPopupModal'),
);
const ModalsGroup = dynamic(() => import('../components/Modal/ModalsGroup'));

if (typeof window !== 'undefined') {
  window.Sentry?.init(setupSentry(getEnv('SENTRY_DSN')));
  window.Sentry?.setExtra('client_id', Cookies.get('sls_client_id'));

  NProgress.configure({
    minimum: 0.2,
    showSpinner: false,
  });

  analyticsInitialize();
  prepareWebVitals();
}

function initRouterEvents() {
  Router.events.on('routeChangeStart', () => NProgress.start());
  Router.events.on('routeChangeComplete', (url: string) => {
    NProgress.done();
    analyticsPageView(url);
  });
  Router.events.on('routeChangeError', () => NProgress.done());
}

async function setupDevWorker() {
  if (getEnv() === 'development' && typeof window === 'undefined') {
    const { mockDevServer } = await import('../test/dev-server');
    await mockDevServer.listen();
  }

  if (
    getEnv() === 'development' &&
    typeof window !== 'undefined' &&
    window.location.protocol.startsWith('https')
  ) {
    const { worker } = await import('../test/dev-worker');
    await worker.start();
  }
}

class MyApp extends App<InitialData> {
  static async getInitialProps(appCtx) {
    if (appCtx.ctx.req) {
      return (
        await import('@sls-react/lib/initialData/getInitialServerProps')
      ).default(appCtx);
    } else {
      return getInitialClientProps(appCtx);
    }
  }

  constructor(props) {
    super(props);

    if (typeof window !== 'undefined') {
      setupDevWorker();

      setCacheOnClient(props);
    }
  }

  componentDidCatch(error, errorInfo) {
    window.Sentry?.withScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key]);
      });

      window.Sentry?.captureException(error);
    });

    super.componentDidCatch?.(error, errorInfo);
  }

  componentDidMount() {
    initRouterEvents();

    // Add client ID on the client as well, in case the initial document response was cached
    if (!Cookies.get('sls_client_id' as KnownCookie)) {
      Cookies.set('sls_client_id' as KnownCookie, uuidv4());
    }
  }

  render() {
    const { Component, pageProps, dehydratedState, ...props } = this.props;

    return (
      <ReactQueryApp pageProps={pageProps} dehydratedState={dehydratedState}>
        <ReactQueryDevtools />
        <GlobalContexts {...props}>
          <ScrollRestoration />
          <Component {...pageProps} />

          <GlobalStyle />
          {typeof window !== 'undefined' && (
            <>
              <ZenDeskScript />
              <ModalsGroup />
              <AutomaticPopupModal />
            </>
          )}
        </GlobalContexts>
      </ReactQueryApp>
    );
  }
}

export default wrapper.withRedux(MyApp);
